import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getGalleryManifest, loadGalleryTexture, preloadGalleryManifest } from '../gallery-assets.js';
import { onArtworkTransitionStart } from '../artwork-transition.js';

// Shared-rig contract (see the framing addendum). 'forbidden': one quad, one
// photograph. There is nothing here to light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'gallery',
  family: 'photographic',
  energyLevel: 0.10,
  bpmMin: 40,
  bpmMax: 140,
  movement: 0.10,
  danceability: 0.30,
  tonality: 0.90,
  prefers: { dynamics: [0.35, 1.0], percussiveness: [0.0, 0.40], density: [0.0, 0.40] },
  enabled: true
};

// Gallery: one photograph, full frame, animated so far below the threshold of
// perception that a viewer should feel the image is alive without being able
// to say what moves.
//
// The whole file is organised around one rule: animate the LIGHT and the POINT
// OF VIEW, never the subject. No warp, no filter, no recolouring. Where a
// constant was a judgement call it was set to the weaker value.
//
// --- why the grain is here and not left to the shared pass --------------------
// scene-manager.js's filmic pass already applies a 2% grain to every frame of
// every visualizer, so the first version of this file simply kept it and did
// not implement layer 4 at all. Measured, that was wrong twice over.
//
// The shared grain is ADDITIVE in linear light and scaled by mix(1, 0.25,
// luminance) - most grain in the blacks, least in the brights. Both halves of
// that are backwards for a photograph. Additive-in-linear means a fixed
// 0.0085 perturbation, and near black the sRGB transfer has a slope of about
// 13, so that lands as roughly 28 levels of visible noise in the shadows of a
// dark photograph - which is what it looked like: speckle in the trees. And
// the falloff puts the LEAST grain in bright smooth skies, which is the one
// place 8-bit banding actually shows.
//
// So the filmic grain is turned off for this visualizer and layer 4 is done
// here instead, MULTIPLICATIVELY. A relative perturbation stays roughly
// constant once through the sRGB power law, so 2% is about half a display
// level everywhere - enough to dither a gradient, far too little to read as
// noise, and the same amount in the sky as in the trees.
//
// --- what this file asks the pipeline to STOP doing --------------------------
// That same shared pass also applies chromatic aberration and a 45% vignette,
// and the composer applies bloom and an ACES curve. All four are right for the
// rest of the library and all four are exactly what this brief forbids: they
// are lens defects simulated on top of an image that already went through a
// real lens, and a second grade on top of the photographer's own. There is no
// way to opt out of them from inside a visualizer, so scene-manager grew a
// per-visualizer override table (POST_DEFAULTS / _applyPostProfile) and this
// is its first and so far only user. Everything not named below keeps the
// house value.
export const POST_PROFILE = {
  aberration: 0.0,
  // Not zero: a trace of falloff is how a photograph is normally printed, and
  // it is what stops the corners of a bright sky from reading as a flat panel.
  // "Marked" is what the brief rules out, and 0.45 is marked.
  vignette: 0.10,
  // Off: the wrong curve for a photograph, and replaced by this file's own
  // layer 4 - see the header.
  grain: 0.0,
  // A photograph carries its own halation. Blooming it again turns a bright
  // sky into a haze, which is a filter by any other name.
  bloomStrength: 0.0,
  bloomThreshold: 0.95,
  toneMapping: THREE.NoToneMapping,
  toneMappingExposure: 1.0
};

// --- framing ------------------------------------------------------------------
// Every source is landscape and NARROWER than 16:9 - of the sixteen, fourteen
// are 3:2, one is 4:3 and one is 1.563 - so a cover fit matches the width and
// crops the height, which leaves exactly zero horizontal overflow for the
// drift to move into. All of the margin therefore has to be bought here, by
// sampling a window smaller than the cover. coverFor() itself is symmetric and
// would handle a source wider than the screen correctly; it is the MARGIN
// reasoning below, not the fit, that assumes narrower.
//
// 1.24 puts 12% of the frame width off each side. That is the brief's figure
// and it is not arbitrary: the drift is +/-2.2% and the zoom +/-1.8%, so 4%
// in the worst case - and 12% is exactly what ?galboost=3 consumes, which is
// the point at which a diagnostic should still not show an edge.
//
// It is not free. At 1.24 over a 3:2 source about 55% of the photograph's area
// survives into the frame, and 49% for the one 4:3 source. That is the price
// of "no edge is ever visible" on images that do not share the screen's
// aspect, and it is why the sources are 4K: what is left is still over 2500px
// wide.
const OVERSCALE = 1.24;

// --- the four layers, all deliberately weak ------------------------------------
// 1. Framing drift. Periods in seconds, mutually incommensurable, so the path
//    through them never closes. No rotation at any amplitude: the eye finds a
//    rotating horizon instantly, which is the one thing this must not do.
const DRIFT_AMP = 0.022;          // of frame width
const DRIFT_PERIOD_X = 71.0;
const DRIFT_PERIOD_Y = 109.0;
const ZOOM_PERIOD = 143.0;
const ZOOM_AMP_MIN = 0.014;       // bands.low moves the zoom between these
const ZOOM_AMP_MAX = 0.024;

// 2. Exposure breathing. The most effective of the four by a distance: the eye
//    reads a slow global exposure change as presence rather than as movement,
//    so it buys the most life for the least attention.
const EXPOSURE_AMP = 0.04;
const EXPOSURE_PERIOD = 23.0;

// 3. Directional light - a broad soft band travelling along the axis the
//    photograph's own light already runs on (measured by build-gallery.mjs,
//    see its fitLightDirection). Never against it.
// Where the rig stops reaching. Below HEAD_LO a pixel takes the light in full;
// above HEAD_HI it takes none, so highlights are never flattened. See the
// measurement at the head term itself for why these are not the original
// 0.32/0.78.
const HEAD_LO = 0.55;
const HEAD_HI = 0.97;
// Raised from 0.11. This band is the only light in the whole file that
// TRAVELS, and at tier 0 it is now the only one at all for weaker devices to
// fall back on if the pair below ever has to be dropped again - so it is the
// one that has to carry "a light is moving across this photograph".
const LIGHT_AMP = 0.17;
const LIGHT_PERIOD = 61.0;
// How far along the axis the band travels, in aspect-corrected half-frames.
// The frame reaches 0.89, so at 2.6 the band is far enough off the edge at the
// wrap that the jump contributes under 0.03% - which is what lets the sweep
// run one way only, as a passing cloud does, instead of oscillating back and
// forth across a photograph whose light has one direction.
const SWEEP_TRAVEL = 2.6;

// 4. Grain. Multiplicative, so it is a constant RELATIVE perturbation and
//    therefore about half a display level across the whole tonal range - see
//    the header for why that matters here and an additive one does not work.
const GRAIN = 0.02;

// --- reactivity ---------------------------------------------------------------
// The AMBIENT level is still deliberately hard to name: bands.mid moves it by
// at most 5%, and only through an 800ms smoother. The rig below it (see "the
// lighting rig") is the part that is meant to be seen - it is struck rather
// than driven, so what reads is the music's events, not its level.
// Left near where it was. The rig below now carries the reactivity the brief
// asked to be visible, so the AMBIENT level can stay in the register this file
// wants it in - and unlike the rig it is a flat global multiply with no
// headroom weighting, so every point of it lands straight on the highlights.
const MUSIC_EXPOSURE_AMP = 0.04;
const TAU_EXPOSURE = 0.80;
const TAU_ZOOM = 1.60;
const DYNAMICS_AMP_MIN = 0.70;
const DYNAMICS_AMP_MAX = 1.15;
// progress -> how strongly the travelling light reads, weakest at the two ends
// of a track.
const LIGHT_GAIN_MIN = 0.55;
const LIGHT_GAIN_MAX = 1.0;

// --- exposure normalisation ----------------------------------------------------
// Two photographs in a row should not differ wildly in density. Matching them
// outright would not do: the sources run from a mean luma of 0.087 (a night
// sky) to 0.576, and pulling the night sky up by 4.2x would destroy the
// photograph rather than normalise it. The correction is therefore taken to a
// fractional power and then clamped hard - a nudge, never an equalisation.
const EXPOSURE_MATCH_POWER = 0.35;
const EXPOSURE_MATCH_MIN = 0.85;
const EXPOSURE_MATCH_MAX = 1.18;

// --- the cover's own patch ------------------------------------------------------
// A very wide, very soft darkening under the album cover. Its AMOUNT is
// measured, not fixed: the 32x18 thumbnail says how bright and how busy the
// photograph is exactly where the cover falls, and a quiet dark passage there
// needs none of it. That is what keeps it from ever reading as a shape, which
// is the acceptance criterion.
const DARKEN_MAX = 0.18;
// Gaussian in units of the safe rectangle: 0.70 of full strength at the edge
// of the rectangle, a quarter at twice its size, gone by three times. Far
// softer than the shared safeZone chunk's rounded box, per the brief.
const DARKEN_FALLOFF = 0.35;

// --- the lighting rig -----------------------------------------------------------
// What used to be here were two threads undulating along the top and bottom
// edges. They were removed on request: whatever they were doing musically,
// they read as two horizontal rules laid over a photograph, which is a piece
// of interface and not a light.
//
// What replaces them keeps the one genuinely good idea in them - resonance.js's
// rule that energy enters ONLY on the RISE of a band envelope and leaves only
// by damping - and spends it on LIGHT falling across the image instead of on
// geometry at its edges. So a sustained loud passage does not sit there
// glowing: the rig is struck on an attack and rings out. And the split the two
// threads had (bass at the bottom, treble at the top) becomes the split a
// lighting setup actually has: a warm key and a cool fill.
//
// Four sources, four different musical signals, and deliberately not the beat
// grid - nothing here is driven by BPM:
//
//   ambient    bands.mid ....... the overall level in the room
//   key        bands.low rises .. struck, warm, on the side the photograph's
//                                 own light already comes from
//   fill       bands.high rises . struck, cool, opposite the key
//   sweep      bands.mid ........ a grazing band whose travel RATE it sets
//   quality    character.percussiveness .. how hard or diffuse all of them are
//   level      director.intensity ........ how strongly the whole rig reads
//
// Both flares sit on the axis build-gallery.mjs measured for each photograph
// (fitLightDirection), so the rig always agrees with the light already in the
// picture rather than fighting it.
const KEY_DIST = 0.62;
const FILL_DIST = 0.70;
// Light QUALITY, from character.percussiveness: a percussive track gets a
// defined source, a legato one a broad wash. This is the axis that makes the
// rig feel like it is responding to the KIND of music rather than its level.
const KEY_SIGMA_SOFT = 0.85;
const KEY_SIGMA_HARD = 0.45;
const FILL_SIGMA_SOFT = 0.95;
const FILL_SIGMA_HARD = 0.55;
const SWEEP_SIGMA_SOFT = 0.62;
const SWEEP_SIGMA_HARD = 0.30;
// Near-neutral on purpose. These are colour TEMPERATURES, not a grade: a warm
// key at full strength lifts red 22% and blue 18%, which reads as the light
// having a direction and a warmth. Anything more saturated would be the
// recolouring this file's header rules out, and it would still be wrong -
// the photograph already has its own white balance.
const KEY_TINT = [1.00, 0.93, 0.80];
const FILL_TINT = [0.84, 0.93, 1.00];
// Raised from 0.22/0.16. The rig was reported as far too static even after
// the synthesised envelopes above started striking it - correct diagnosis,
// but at the old gains a full strike moved the frame's mean by about one
// display level, which is not something a viewer can see. These are still
// multiplicative and still weighted by headroom, so the highlights hold.
const KEY_GAIN = 0.34;
const FILL_GAIN = 0.26;
// Struck, not driven - the rise-only physics inherited from the threads.
//
// These four were retuned against MEASURED radiopi envelopes, because the
// originals were sized for a signal this one is not. Sampled on the kiosk over
// 20s of a real track: bands.low rose on only 46 frames out of 436, and when it
// rose the MEDIAN rise was 0.355 with a maximum of 0.93. radiopi's envelopes
// are 100ms buckets - they arrive as about two large discrete steps per second,
// not as a smooth curve.
//
// Against that signal the old constants could only saturate. One bucket times
// an excite gain of 2.4 is 0.85 of the flare's whole range, and at two of those
// per second against a damping of 0.55 (a 1.8s time constant) the flare never
// came back down: measured live on the Pi it sat between 0.73 and 1.00, a 27%
// modulation on a light that is supposed to be struck and ring out. That is
// what "far too static" was, and it is also why raising KEY_GAIN did nothing -
// a brighter constant is still a constant.
//
// Retuned so the flare rings out between buckets: damping 2.2 (a 0.45s time
// constant, shorter than the gap), excite 1.5 so one bucket is a strike and not
// a saturation, and a threshold at 0.12 which still admits about three quarters
// of the real rises while ignoring ripple. Simulated against the same recorded
// 20s, that takes the flare from 0.63-1.00 to 0.09-1.00 - the modulation the
// rig is built around, and about five times the light movement at the same gain.
const FLARE_RISE_THRESHOLD = 0.12;
const FLARE_EXCITE_GAIN = 1.5;
const FLARE_DAMPING = 2.2;
// A floor, for the same reason the threads had one: a rig at zero is a
// photograph with the lights off, and the link to the music should not vanish
// in a quiet passage, only go quiet with it. Lowered with the rest: at 0.16 it
// was itself holding a third of the range up.
const FLARE_REST = 0.08;
// Where the two flares drift, slowly and on incommensurable periods, so the
// rig never settles into a pose.
const FLARE_DRIFT = 0.22;
const FLARE_PERIOD_KEY = 89.0;
const FLARE_PERIOD_FILL = 127.0;
// bands.mid -> how fast the grazing band travels. Integrated into its own
// phase rather than multiplied into a clock, so a change of rate never
// teleports the band across the frame.
// Raised from 0.65/1.85. At 0.65 the band needed 94s for one cycle and about
// 69s to cross the visible frame, which is slower than anyone watches a
// photograph for - the light was travelling, but no one was going to be
// looking at the same image long enough to see it arrive. 1.2-3.0 puts a
// crossing at roughly 35s down to 15s, still a passing cloud rather than a
// searchlight.
const SWEEP_RATE_MIN = 1.20;
const SWEEP_RATE_MAX = 3.00;
// director.intensity -> how strongly the whole rig reads. An intro is lit
// softly, a drop is lit hard, and that is a structural response no per-frame
// band envelope can give.
const RIG_GAIN_MIN = 0.55;
const RIG_GAIN_MAX = 1.15;
const TAU_RIG = 1.10;
const TAU_SWEEP_RATE = 2.20;

// --- when there is no analysis to play the rig ---------------------------------
// director.bands is {0,0,0} for any track radiopi has not analysed (see
// director.js's NEUTRAL_BANDS), which on the radio page is most of them. Every
// coupling above reads those bands, and the rig takes energy ONLY from one
// rising - so all-zeros does not mean "lit dimly", it means the rig is never
// struck at all and the ambient level, the zoom travel and the sweep rate are
// all pinned to a constant. The photograph then sits under a switched-off rig
// with nothing but a 71-second drift: a still image, which is what "far too
// static" was.
//
// So the envelopes are SYNTHESISED when the real ones are absent, and the rig
// itself is left exactly as it is - one substitution upstream instead of a
// second excitation path bolted alongside. Two sources, best first:
//
//   a beat grid .... swells on the bar and the half-bar, so the light follows
//                    the music even with no spectral analysis behind it
//   nothing ........ three pulse trains on incommensurable periods, so the rig
//                    still plays and still never repeats
//
// The synthesised envelopes, for every track radiopi has not analysed.
//
// They are built to look like the REAL signal rather than like a nice curve,
// and that is the whole design. radiopi delivers 100ms buckets, so its
// envelopes step: measured on the kiosk, about two large discrete jumps per
// second, median 0.355. The rig's threshold is set for that shape. A smooth
// ramp of the same amplitude spread over a dozen frames never clears it - and
// worse, whether it clears it depends on the frame rate, so the same code
// would have played on a 60Hz panel and gone silent on a 120Hz one.
//
// So these step too: a trigger sets the envelope instantly to a peak, and it
// decays from there. One frame carries the whole rise, which clears any
// threshold at any frame rate, and the decay afterwards is what moves the
// ambient exposure, the zoom travel and the sweep rate.
const SYNTH_BASE = 0.10;
const SYNTH_DECAY = 1.9;          // per second, back toward the base
// Triggers, best source first: the bar if there is a beat grid, otherwise
// three incommensurable periods so the rig plays itself and never loops.
const SYNTH_PERIOD_LOW = 4.7;
const SYNTH_PERIOD_MID = 3.1;
const SYNTH_PERIOD_HIGH = 6.3;

// --- rotation --------------------------------------------------------------------
// Never repeat an image seen in the last four tracks. With sixteen sources
// that leaves twelve candidates - the depth is kept at four rather than raised
// with the library because what it is protecting against is noticing a REPEAT,
// and four tracks is already past where anyone would.
//
// The count is read from the manifest, never from a constant here: adding a
// photograph to assets/4k/ and running build:assets is the whole procedure.
// The two duplicate sources that were in that directory (byte-identical copies
// of urban-vintage and wade-meng, from a double download) got two distinct
// slugs and therefore two manifest entries each, which is exactly the state
// this rule exists to prevent - they were removed rather than special-cased.
const HISTORY_DEPTH = 4;
const FADE_MS = 2000;
// If the cover art does not change between two tracks (two tracks off one
// album), artwork-transition.js never fires - see _armTransition. The
// photograph still has to change, so a track change arms this deadline and
// whichever comes first wins.
const TRANSITION_FALLBACK_MS = 1400;

const TAU = 6.283185307179586;

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

function approach(current, target, deltaTime, tau) {
  return current + (target - current) * (1 - Math.exp(-deltaTime / tau));
}

// artwork-transition.js's subscriber list has no unsubscribe, so subscribing
// per instance would leak one listener per visualizer swap for the life of the
// page. One module-level subscription instead, routed to whichever instance is
// currently live (there is only ever one - SceneManager disposes the outgoing
// visualizer before init'ing the next).
let activeInstance = null;
onArtworkTransitionStart(function (detail) {
  if (activeInstance) activeInstance._onArtworkTransition(detail);
});

const vertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    // Fullscreen quad in clip space. The scene's slow camera drift is a fifth
    // animation layer this brief did not ask for, and it is larger than any of
    // the four it did.
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

function buildFragmentShader(opts) {
  return `
  precision mediump float;

  uniform sampler2D uImageA;
  uniform sampler2D uImageB;
  uniform float uMix;
  uniform vec2 uCoverA;
  uniform vec2 uCoverB;
  uniform float uExposureA;
  uniform float uExposureB;
  // Phases in [0,1), each wrapped on the CPU in double precision. NOT a raw
  // elapsed time: a track running ten minutes reaches t=600, where a mediump
  // float resolves about 0.6 - which would quantise sin(t*TAU/23) into steps
  // and turn the gentlest layer here into the most visible one.
  uniform vec4 uPhase;      // x drift-x, y drift-y, z zoom, w exposure
  uniform float uSweep;     // travelling light, also [0,1)
  uniform float uAmp;
  uniform float uZoomAmp;
  uniform float uMusicExposure;
  uniform vec2 uLightDir;
  uniform float uLightGain;
  uniform float uDarken;
  uniform float uGrainSeed;
  // The rig. Positions are aspect-corrected screen space; strengths are the
  // two flare oscillators, already stepped on the CPU.
  uniform vec2 uKeyPos;
  uniform vec2 uFillPos;
  uniform vec2 uFlare;      // x key, y fill - current amplitudes
  uniform vec3 uSigma;      // x key, y fill, z sweep - light quality
  uniform float uRigGain;

  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;

  varying vec2 vUv;

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  // One photograph, through the shared framing. The window is CLAMPED inside
  // the texture rather than trusted to stay there: criterion 2 ("no edge is
  // ever visible") then holds at any amplitude, ?galboost=3 included, instead
  // of holding only as long as nobody edits the constants.
  vec3 sampleImage(sampler2D tex, vec2 cover, vec2 p, vec2 drift, float zoom) {
    vec2 w = cover / (${OVERSCALE.toFixed(4)} * zoom);
    vec2 lim = max(vec2(0.0), 0.5 - 0.5 * w);
    vec2 c = clamp(drift * w, -lim, lim);
    return texture2D(tex, p * w + 0.5 + c).rgb;
  }

  void main() {
    vec2 p = vUv - 0.5;

    // 1. FRAMING DRIFT
    vec2 drift = vec2(
      sin(uPhase.x * ${TAU.toFixed(7)}),
      sin(uPhase.y * ${TAU.toFixed(7)})
    ) * (${DRIFT_AMP.toFixed(4)} * uAmp);
    float zoom = 1.0 + sin(uPhase.z * ${TAU.toFixed(7)}) * uZoomAmp * uAmp;

    vec3 col = sampleImage(uImageA, uCoverA, p, drift, zoom) * uExposureA;
    // Uniform branch - whole frame takes the same side, so the second
    // photograph is only ever read during the two seconds of a crossfade.
    if (uMix > 0.0) {
      col = mix(col, sampleImage(uImageB, uCoverB, p, drift, zoom) * uExposureB, uMix);
    }

    // 2. EXPOSURE BREATHING
    col *= 1.0 + sin(uPhase.w * ${TAU.toFixed(7)}) * (${EXPOSURE_AMP.toFixed(4)} * uAmp)
               + uMusicExposure;

${opts.directionalLight ? `
    // 3. THE LIGHTING RIG - a grazing sweep plus a warm key and a cool fill,
    //    all of them on the axis the photograph's own light already runs on.
    vec2 sp = vec2(p.x * uAspect, p.y);

    // Light added to an area that is already near the top of the medium's
    // range does nothing except clip it, and a blown sky on a photograph is a
    // defect rather than an effect. So the whole rig is weighted by how much
    // headroom this pixel has left. It also happens to be what makes the light
    // read as falling ON the subject instead of as a multiplier over the
    // frame: the shadows and midtones take it, the highlights hold.
    // Measured on the BRIGHTEST CHANNEL, not on luma. Luma was the first
    // version and it leaks: a lamp at (250, 180, 120) has a luma of only 0.75,
    // so it still collected most of the rig and its red channel clipped
    // anyway. The channel that is about to hit the ceiling is the one that has
    // to be asked, and it costs two max().
    //
    // The rolloff also has to FINISH before white rather than at it, so the
    // highlights hold and cannot be flattened.
    //
    // The window was 0.32..0.78, and the claim above it was that this "costs
    // nothing visible". Measured on a real frame from this gallery, it costs
    // almost everything: 88% of the pixels sit above 0.32 and are therefore
    // already losing the rig, and 28% are above 0.78 and receive none of it at
    // all. These photographs are far brighter than that window assumed, so the
    // lighting had almost no surface left to act on - which is what "il manque
    // un effet d'eclairage" actually was. Opened to ${HEAD_LO}..${HEAD_HI}: the
    // midtones a photograph is mostly made of now take the light, and only the
    // genuine highlights still hold.
    float head = 1.0 - smoothstep(${HEAD_LO.toFixed(2)}, ${HEAD_HI.toFixed(2)},
                                  max(col.r, max(col.g, col.b)));
    float rig = uRigGain * uAmp * head;

    // THE GRAZING BAND, at every tier including 0. This is the only light here
    // that actually travels, and it is one exp() - so it was the wrong thing
    // to be dropping on the weakest device. Tier 0 used to lose the whole rig,
    // which on the television left the picture with a slow Ken Burns and no
    // changing light at all; that is the report this moved for. Key and fill
    // below stay tiered: they are two more exps plus their tints, and they
    // drift rather than travel, so their absence costs far less.
    float d = (dot(sp, uLightDir) - (uSweep * 2.0 - 1.0) * ${SWEEP_TRAVEL.toFixed(3)}) / uSigma.z;
    col *= 1.0 + exp(-d * d * 0.5) * (${LIGHT_AMP.toFixed(4)} * uLightGain * rig);

    // Key and fill. Multiplied, never added: a light that multiplies cannot
    // put colour into a black, which is what keeps these off the photograph's
    // shadows entirely instead of fogging them.
    vec2 dk = (sp - uKeyPos) / uSigma.x;
    vec2 df = (sp - uFillPos) / uSigma.y;
    col *= 1.0 + vec3(${KEY_TINT[0].toFixed(2)}, ${KEY_TINT[1].toFixed(2)}, ${KEY_TINT[2].toFixed(2)})
                 * (exp(-dot(dk, dk) * 0.5) * uFlare.x * ${KEY_GAIN.toFixed(3)} * rig);
    col *= 1.0 + vec3(${FILL_TINT[0].toFixed(2)}, ${FILL_TINT[1].toFixed(2)}, ${FILL_TINT[2].toFixed(2)})
                 * (exp(-dot(df, df) * 0.5) * uFlare.y * ${FILL_GAIN.toFixed(3)} * rig);
` : `
    // Tier 0 keeps the ambient level and the travelling band above, and drops
    // only the key/fill pair - see TIER_LIGHT.
`}

    // 4. GRAIN, at one sample per output pixel - the finest there is.
    col *= 1.0 + (hash12(vUv * vec2(1920.0, 1080.0) + uGrainSeed) - 0.5) * ${GRAIN.toFixed(4)};

    // The cover's patch. Multiplied in, never added: darkening a photograph is
    // the only local change here that cannot introduce a colour of its own.
    vec2 s = p * 2.0 * vec2(uAspect, 1.0);
    vec2 q = (s - uSafeCenter) / max(uSafeHalfSize, vec2(0.001));
    col *= 1.0 - uDarken * exp(-dot(q, q) * ${DARKEN_FALLOFF.toFixed(3)});

    gl_FragColor = vec4(col, 1.0);
  }
`;
}

// Index = quality tier. The variant width itself is gallery-assets.js's
// TIER_WIDTH; this is the only thing tiered inside the shader.
//
// It now gates the KEY/FILL PAIR ONLY. The travelling band is unconditional:
// it is the single element that gives the composition a light that changes,
// and dropping it left tier 0 with no lighting animation whatever.
// Every tier now, including 0. The pair is two exp() and two vec3 multiplies
// on a shader whose cost is one texture fetch - it was never what made a weak
// device slow, and dropping it is what left the radio page (which measures
// tier 0) with a photograph lit by nothing but a single band crossing once a
// minute. The sweep had already been moved down here for exactly this reason;
// this finishes that move.
const TIER_LIGHT = [true, true, true];
// Separate from TIER_LIGHT, which it used to ride on. Once the key/fill pair
// moved down to tier 0 that shared flag would have started animating the grain
// there too - a different change, not measured here, and not one this edit is
// making. Kept on its own switch at its original setting.
const TIER_ANIMATED_GRAIN = [false, true, true];

export class GalleryVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : 1;
    this._tier = TIER_LIGHT[tier] != null ? tier : 1;
    this.postProcessing = POST_PROFILE;

    const forced = parseInt(urlParam('img'), 10);
    this._forcedIndex = Number.isFinite(forced) ? forced : null;
    this._freeze = urlParam('galfreeze') === '1';
    // ?galsynth=0 - hand the rig the raw director.bands even when they are the
    // all-zero neutral set, which is what this file did before the fallback
    // existed. The A/B for judging it: the difference is a rig that plays
    // against a rig that is switched off.
    this._noSynth = urlParam('galsynth') === '0';
    // Synthesised envelope state - see SYNTH_BASE.
    this._synth = { low: SYNTH_BASE, mid: SYNTH_BASE, high: SYNTH_BASE };
    this._synthPhase = { low: null, mid: null, high: null };
    const boost = parseFloat(urlParam('galboost'));
    this._boost = boost > 0 ? boost : 1;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._manifest = getGalleryManifest();

    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uImageA: { value: null },
      uImageB: { value: null },
      uMix: { value: 0 },
      uCoverA: { value: new THREE.Vector2(1, 1) },
      uCoverB: { value: new THREE.Vector2(1, 1) },
      uExposureA: { value: 1 },
      uExposureB: { value: 1 },
      uPhase: { value: new THREE.Vector4(0, 0, 0, 0) },
      uSweep: { value: 0 },
      uAmp: { value: 1 },
      uZoomAmp: { value: (ZOOM_AMP_MIN + ZOOM_AMP_MAX) * 0.5 },
      uMusicExposure: { value: 0 },
      uLightDir: { value: new THREE.Vector2(0, 1) },
      uLightGain: { value: LIGHT_GAIN_MIN },
      uDarken: { value: 0 },
      uGrainSeed: { value: 0 },
      uKeyPos: { value: new THREE.Vector2() },
      uFillPos: { value: new THREE.Vector2() },
      uFlare: { value: new THREE.Vector2(FLARE_REST, FLARE_REST) },
      uSigma: { value: new THREE.Vector3(KEY_SIGMA_SOFT, FILL_SIGMA_SOFT, SWEEP_SIGMA_SOFT) },
      uRigGain: { value: RIG_GAIN_MIN }
    });

    // A 1x1 mid grey stands in until the first photograph has decoded, and is
    // also what uImageB points at whenever no crossfade is running: a
    // sampler2D must always have something bound, and pointing it at the live
    // photograph instead would make the "one texture" accounting ambiguous.
    this._blank = new THREE.DataTexture(new Uint8Array([28, 28, 30, 255]), 1, 1, THREE.RGBAFormat);
    this._blank.needsUpdate = true;
    this._uniforms.uImageA.value = this._blank;
    this._uniforms.uImageB.value = this._blank;

    this._geometry = new THREE.PlaneGeometry(1, 1);
    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: vertexShader,
      fragmentShader: buildFragmentShader({ directionalLight: TIER_LIGHT[this._tier] }),
      transparent: false,
      depthTest: false,
      depthWrite: false
    });
    this._mesh = new THREE.Mesh(this._geometry, this._material);
    this._mesh.frustumCulled = false;
    this._mesh.renderOrder = -1000;
    scene.add(this._mesh);

    this._time = 0;
    this._musicExposure = 0;
    this._zoomAmp = (ZOOM_AMP_MIN + ZOOM_AMP_MAX) * 0.5;
    this._history = [];
    this._current = null;      // { entry, texture }
    this._incoming = null;     // { entry, texture }
    this._fade = 0;            // 0..1 while a crossfade runs
    this._loadToken = 0;
    this._pendingTrackId = null;
    this._pendingDeadline = 0;
    this._darkenKey = '';

    // The two struck lights. Allocated once; nothing here allocates per frame.
    // Their amplitude decays every frame and is only ever pushed up by a band
    // RISE - see the rig header.
    this._flare = { key: FLARE_REST, fill: FLARE_REST };
    // Last frame's band envelopes. The rig is excited by the DIFFERENCE, so
    // this is not a cache - it is half the mechanism.
    this._prevBands = { low: 0, mid: 0, high: 0 };
    this._rigGain = RIG_GAIN_MIN;
    this._sweepRate = 1;
    this._sweepPhase = 0;

    activeInstance = this;
    if (this._manifest) this._beginChange();
    else {
      // LE MANIFESTE N'EST PAS ENCORE LÀ. Il était lu UNE SEULE FOIS, ici, et
      // s'il manquait à cet instant le visualiseur restait gris pour toute son
      // apparition - définitivement, sans une erreur en console. Deux façons
      // d'y arriver : une page de boot qui ne préchargeait pas le manifeste
      // (il en manquait cinq sur huit), et la course toute bête où gallery est
      // choisi avant que le fetch ait répondu. On le redemande donc, et on
      // démarre quand il arrive.
      const jeton = this._loadToken;
      preloadGalleryManifest().then((m) => {
        if (!m || activeInstance !== this || this._loadToken !== jeton) return;
        this._manifest = m;
        this._beginChange();
      });
    }
  }

  // --- image choice -----------------------------------------------------------
  _pickEntry() {
    const images = this._manifest.images;
    if (this._forcedIndex != null) {
      return images[((this._forcedIndex % images.length) + images.length) % images.length];
    }
    const eligible = images.filter((img) => this._history.indexOf(img.id) === -1);
    const pool = eligible.length > 0 ? eligible : images;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  _remember(id) {
    this._history.push(id);
    while (this._history.length > HISTORY_DEPTH) this._history.shift();
  }

  // Loads the next photograph and, once it has decoded, starts the crossfade.
  // Nothing is loaded ahead of this moment: the point of loading here rather
  // than during the previous track is that only one photograph is ever
  // resident, which is what keeps this inside a Pi's texture budget.
  _beginChange(retry) {
    if (!this._manifest) return;
    const entry = this._pickEntry();
    const token = ++this._loadToken;
    loadGalleryTexture(entry, this._tier).then((result) => {
      // A newer track change overtook this load. Drop it, texture included -
      // otherwise this is exactly where a leak would live.
      if (token !== this._loadToken || !this._scene) {
        result.texture.dispose();
        return;
      }
      this._remember(entry.id);
      if (!this._current) {
        // First image of the session: no crossfade to run, nothing to fade
        // from, and a fade up from grey would be the one hard cut in the file.
        this._current = { entry: entry, texture: result.texture };
        this._uniforms.uImageA.value = result.texture;
        this._uniforms.uCoverA.value.copy(coverFor(entry.aspect, this._camera.aspect));
        this._uniforms.uExposureA.value = exposureFor(entry, this._manifest);
        this._applyLook(entry);
        return;
      }
      this._incoming = { entry: entry, texture: result.texture };
      this._uniforms.uImageB.value = result.texture;
      this._uniforms.uCoverB.value.copy(coverFor(entry.aspect, this._camera.aspect));
      this._uniforms.uExposureB.value = exposureFor(entry, this._manifest);
      this._fade = 0.0001;
    }).catch((err) => {
      if (token !== this._loadToken || !this._scene) return;
      // Remember it even though it never arrived: otherwise the same broken
      // file is the most likely pick again next track, and the visualizer sits
      // on one photograph indefinitely while logging. One retry, then the
      // image on screen simply stays - which is the right failure, not a hole.
      this._remember(entry.id);
      console.error('[gallery] ' + entry.id + ' failed to load' +
        (retry ? ', keeping the photograph on screen' : ', trying another'), err);
      if (!retry) this._beginChange(true);
    });
  }

  // The photograph's own light direction, lerped across a crossfade rather than
  // switched: the band is mid-sweep when the images change and a direction that
  // snapped would be the most visible event in the whole visualizer.
  _applyLook(entry) {
    const d = entry.lightDir;
    // A photograph with no measurable gradient (a flat sky, a studio backdrop)
    // reports a zero vector rather than a direction fitted to noise.
    if (d && (d[0] !== 0 || d[1] !== 0)) this._uniforms.uLightDir.value.set(d[0], d[1]);
    else this._uniforms.uLightDir.value.set(0, 1);
  }

  // --- track changes ------------------------------------------------------------
  onTrackChange(profile) {
    // Arms the change; the crossfade itself starts on the artwork transition
    // so the photograph and the cover cross at one instant. The deadline is
    // the fallback for two tracks off the same album, where the cover never
    // changes and artwork-transition.js therefore never fires.
    this._pendingTrackId = profile ? profile.trackId : null;
    this._pendingDeadline = this._time + TRANSITION_FALLBACK_MS / 1000;
  }

  _onArtworkTransition(detail) {
    if (this._pendingTrackId === null) return;
    if (detail && detail.trackId != null && detail.trackId !== this._pendingTrackId) return;
    this._pendingTrackId = null;
    this._beginChange();
  }

  // --- the cover's patch ---------------------------------------------------------
  // How bright and how busy the photograph is exactly where the album cover
  // falls, read off the 32x18 thumbnail. A dark quiet passage under the cover
  // needs no help; a bright busy one needs all of DARKEN_MAX. Recomputed only
  // when the image or the measured safe rectangle actually changes - the
  // rectangle is remeasured on a live cadence (see safeZone.glsl.js) but its
  // value almost never moves.
  _updateDarken() {
    const cur = this._current;
    if (!cur || !cur.entry.thumb) { this._uniforms.uDarken.value = 0; return; }
    const c = safeZoneUniforms.uSafeCenter.value;
    const h = safeZoneUniforms.uSafeHalfSize.value;
    const a = safeZoneUniforms.uAspect.value;
    const key = cur.entry.id + '|' + c.x.toFixed(2) + ',' + c.y.toFixed(2) +
      '|' + h.x.toFixed(2) + ',' + h.y.toFixed(2) + '|' + a.toFixed(2);
    if (key === this._darkenKey) return;
    this._darkenKey = key;

    // Safe rectangle: height units -> screen uv -> texture uv, through the
    // same cover+overscale mapping the shader uses, at the nominal (undrifted)
    // framing. The drift is 2.2%, far too small to move which part of the
    // photograph is under the cover.
    const cover = coverFor(cur.entry.aspect, a);
    const toTexX = (su) => (su - 0.5) * cover.x / OVERSCALE + 0.5;
    const toTexY = (sv) => (sv - 0.5) * cover.y / OVERSCALE + 0.5;
    const su0 = 0.5 + (c.x - h.x) / (2 * a), su1 = 0.5 + (c.x + h.x) / (2 * a);
    const sv0 = 0.5 + (c.y - h.y) / 2, sv1 = 0.5 + (c.y + h.y) / 2;
    const x0 = toTexX(su0), x1 = toTexX(su1);
    // Thumbnail rows run top-down, screen v runs bottom-up.
    const y0 = 1 - toTexY(sv1), y1 = 1 - toTexY(sv0);

    const tw = cur.entry.thumbWidth, th = cur.entry.thumbHeight, t = cur.entry.thumb;
    const i0 = Math.max(0, Math.floor(x0 * tw)), i1 = Math.min(tw - 1, Math.ceil(x1 * tw) - 1);
    const j0 = Math.max(0, Math.floor(y0 * th)), j1 = Math.min(th - 1, Math.ceil(y1 * th) - 1);
    let sum = 0, sumSq = 0, n = 0;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const v = t[j * tw + i];
        sum += v; sumSq += v * v; n++;
      }
    }
    if (n === 0) { this._uniforms.uDarken.value = 0; return; }
    const mean = sum / n;
    const variance = Math.max(0, sumSq / n - mean * mean);
    // Two independent reasons the cover needs separating from what is behind
    // it: the passage is bright, or the passage is busy. Either one alone can
    // ask for the full amount.
    const brightness = Math.min(1, Math.max(0, (mean - 0.25) / 0.45));
    const busyness = Math.min(1, Math.sqrt(variance) / 0.16);
    this._uniforms.uDarken.value = DARKEN_MAX * Math.min(1, Math.max(brightness, busyness));
  }

  // The three band envelopes the rest of this file reads - the real ones when
  // radiopi has analysed the track, a synthesised set when it has not. See
  // SYNTH_BASE and the block around it for why the fallback exists and why it
  // is built from pulses.
  //
  // director.hasReliableGrid is exactly "an analysis timeline exists", which is
  // exactly the condition under which director.bands carries real values, so it
  // is the test rather than sniffing the numbers themselves.
  _effectiveBands(profile, director) {
    if (this._noSynth) return director && director.bands;
    if (director && director.hasReliableGrid && director.bands) return director.bands;
    return this._synth;
  }

  // Advances the synthesised envelopes - see SYNTH_BASE. Separate from
  // _effectiveBands because it is a state step and must run exactly once per
  // frame, where reading the envelopes may happen more than once.
  _stepSynthBands(deltaTime, profile) {
    const k = Math.exp(-SYNTH_DECAY * deltaTime);
    const sy = this._synth;
    sy.low = SYNTH_BASE + (sy.low - SYNTH_BASE) * k;
    sy.mid = SYNTH_BASE + (sy.mid - SYNTH_BASE) * k;
    sy.high = SYNTH_BASE + (sy.high - SYNTH_BASE) * k;

    // A trigger is a phase WRAP, which is one unambiguous event per cycle and
    // needs no width tuning - the step is the attack.
    const fired = (key, phase) => {
      const prev = this._synthPhase[key];
      this._synthPhase[key] = phase;
      return prev != null && phase < prev;
    };

    const bar = profile.barPhase;
    const beat = profile.beatPhase;
    if (bar != null && beat != null) {
      // Bass on the downbeat, mids on the half-bar, highs on the "and" of each
      // beat. On the bar rather than every beat: the flares ring out over about
      // half a second now, so a strike per beat would still read as played, but
      // the bar is what gives the light a phrase rather than a pulse.
      if (fired('low', bar)) sy.low = 1.0;
      if (fired('mid', (bar + 0.5) % 1)) sy.mid = 0.85;
      if (fired('high', (beat + 0.5) % 1)) sy.high = 0.75;
      return;
    }

    // Nothing to follow at all - unmetered material, or no BPM yet.
    const t = this._time;
    if (fired('low', (t / SYNTH_PERIOD_LOW) % 1)) sy.low = 1.0;
    if (fired('mid', (t / SYNTH_PERIOD_MID) % 1)) sy.mid = 0.85;
    if (fired('high', (t / SYNTH_PERIOD_HIGH) % 1)) sy.high = 0.75;
  }

  // The rig's own physics, inherited from the threads that used to be here and
  // through them from resonance.js: amplitude decays exponentially and the ONLY
  // thing that adds energy is a band envelope RISING. A loud sustained passage
  // therefore does not hold the lights up - they are struck on the attack and
  // ring out, which is what makes the rig read as played rather than as driven
  // by a level meter.
  _stepRig(deltaTime, bands, character, director) {
    const rise = (name) => {
      const v = bands && bands[name] != null ? bands[name] : 0;
      const r = v - this._prevBands[name];
      this._prevBands[name] = v;
      return r > FLARE_RISE_THRESHOLD ? r : 0;
    };
    const rl = rise('low'), rm = rise('mid'), rh = rise('high');

    // The bass strikes the key, the treble the fill; both take a little of the
    // mid so neither ever sits completely dark.
    const strike = (cur, energy) => {
      let a = cur * Math.exp(-FLARE_DAMPING * deltaTime);
      if (energy > 0) a = Math.min(1, a + energy * FLARE_EXCITE_GAIN);
      return Math.max(a, FLARE_REST);
    };
    this._flare.key = strike(this._flare.key, rl + rm * 0.35);
    this._flare.fill = strike(this._flare.fill, rh + rm * 0.35);

    // Light QUALITY. percussiveness is the one character axis that says what
    // KIND of music this is rather than how loud, and hard-versus-diffuse is
    // the lighting property it maps onto without any interpretation.
    const perc = character && character.percussiveness != null ? character.percussiveness : 0.4;
    this._uniforms.uSigma.value.set(
      KEY_SIGMA_SOFT + (KEY_SIGMA_HARD - KEY_SIGMA_SOFT) * perc,
      FILL_SIGMA_SOFT + (FILL_SIGMA_HARD - FILL_SIGMA_SOFT) * perc,
      SWEEP_SIGMA_SOFT + (SWEEP_SIGMA_HARD - SWEEP_SIGMA_SOFT) * perc);

    // Structural level. director.intensity is the arrangement's own envelope -
    // an intro is lit softly and a drop hard - which is a response no
    // per-frame band value can give.
    const intensity = director && director.intensity != null ? director.intensity : 0.5;
    this._rigGain = approach(
      this._rigGain, RIG_GAIN_MIN + (RIG_GAIN_MAX - RIG_GAIN_MIN) * intensity, deltaTime, TAU_RIG);

    // And bands.mid sets how fast the grazing band travels, integrated into
    // its own phase so a change of rate never moves the band itself.
    const mid = bands && bands.mid != null ? bands.mid : 0.5;
    this._sweepRate = approach(
      this._sweepRate, SWEEP_RATE_MIN + (SWEEP_RATE_MAX - SWEEP_RATE_MIN) * mid,
      deltaTime, TAU_SWEEP_RATE);
    this._sweepPhase = (this._sweepPhase + deltaTime / LIGHT_PERIOD * this._sweepRate) % 1;
  }

  // Both flares sit on the axis measured for THIS photograph, so the rig always
  // agrees with the light already in the picture - the key on the side that
  // light comes from, the fill opposite it - and drifts slowly around it.
  _writeRig(t) {
    const u = this._uniforms;
    const dir = u.uLightDir.value;
    const nx = -dir.y, ny = dir.x; // perpendicular, for the drift
    const dk = Math.sin(t / FLARE_PERIOD_KEY * TAU) * FLARE_DRIFT;
    const df = Math.sin(t / FLARE_PERIOD_FILL * TAU + 1.7) * FLARE_DRIFT;
    u.uKeyPos.value.set(dir.x * KEY_DIST + nx * dk, dir.y * KEY_DIST + ny * dk);
    u.uFillPos.value.set(-dir.x * FILL_DIST + nx * df, -dir.y * FILL_DIST + ny * df);
    u.uFlare.value.set(this._flare.key, this._flare.fill);
    u.uRigGain.value = this._rigGain;
    u.uSweep.value = this._sweepPhase;
  }


  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    // A frozen photograph is the correct rendering of a paused track, and it
    // is also what ?galfreeze=1 exists to compare against.
    if (!paused && !this._freeze) this._time += deltaTime;

    const director = profile.director;
    // Every band read below goes through this, not through director.bands
    // directly: without an analysis those are all zeros and the whole file
    // freezes. See _effectiveBands.
    // Stepped before it is read, and only while running - so a paused track
    // freezes the rig with everything else.
    if (!paused && !this._freeze) this._stepSynthBands(deltaTime, profile);
    const bands = this._effectiveBands(profile, director);
    const character = director && director.character;
    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;

    if (!paused) {
      // bands.mid -> the ambient level, +/-5%, through an 800ms smoother. The
      // smoothing is not a refinement of this coupling, it IS the coupling: an
      // immediate response is what would let a viewer name it as a meter.
      const mid = bands && bands.mid != null ? bands.mid : 0.5;
      this._musicExposure = approach(
        this._musicExposure, (mid - 0.5) * 2 * MUSIC_EXPOSURE_AMP, deltaTime, TAU_EXPOSURE);
      // bands.low -> how far the slow zoom travels.
      const low = bands && bands.low != null ? bands.low : 0.5;
      this._zoomAmp = approach(
        this._zoomAmp, ZOOM_AMP_MIN + (ZOOM_AMP_MAX - ZOOM_AMP_MIN) * low, deltaTime, TAU_ZOOM);
    }

    const amp = (DYNAMICS_AMP_MIN + (DYNAMICS_AMP_MAX - DYNAMICS_AMP_MIN) * dynamics) * this._boost;
    const progress = profile.duration > 0 ? THREE.MathUtils.clamp(profile.progress, 0, 1) : 0.5;

    const u = this._uniforms;
    // Wrapped here, in double precision, so the shader only ever sees [0,1).
    const t = this._time;
    u.uPhase.value.set(
      (t / DRIFT_PERIOD_X) % 1,
      (t / DRIFT_PERIOD_Y + 0.27) % 1,
      (t / ZOOM_PERIOD) % 1,
      (t / EXPOSURE_PERIOD) % 1
    );
    u.uAmp.value = amp;
    u.uZoomAmp.value = this._zoomAmp;
    u.uMusicExposure.value = this._musicExposure * this._boost;
    // Tier 0 holds the grain still: a static dither breaks banding just as
    // well, and a Pi-class panel shows animated noise more than it hides it.
    u.uGrainSeed.value = TIER_ANIMATED_GRAIN[this._tier] ? (t * 60) % 1024 : 0;
    u.uLightGain.value = LIGHT_GAIN_MIN
      + (LIGHT_GAIN_MAX - LIGHT_GAIN_MIN) * Math.sin(Math.PI * progress);

    // The rig. Frozen with everything else on pause and under ?galfreeze=1, so
    // the comparison that criterion 1 asks for stays honest.
    if (!paused && !this._freeze) this._stepRig(deltaTime, bands, character, director);
    this._writeRig(t);

    // The fallback for a track change the cover never announced.
    if (this._pendingTrackId !== null && this._time >= this._pendingDeadline) {
      this._pendingTrackId = null;
      this._beginChange();
    }

    if (this._fade > 0) this._advanceFade(deltaTime, paused);
    this._updateDarken();
  }

  _advanceFade(deltaTime, paused) {
    // A crossfade frozen mid-way by a pause would leave two photographs
    // superimposed, which is the one state this visualizer must never sit in.
    // It finishes on wall time regardless.
    this._fade = Math.min(1, this._fade + (deltaTime * 1000) / FADE_MS);
    this._uniforms.uMix.value = this._fade;
    if (this._fade < 1) return;

    // Landed. B becomes A, and the photograph that was on screen is released
    // HERE - not on the next track change, and not to the garbage collector,
    // which does not free GPU memory.
    const outgoing = this._current;
    this._current = this._incoming;
    this._incoming = null;
    this._uniforms.uImageA.value = this._current.texture;
    this._uniforms.uCoverA.value.copy(this._uniforms.uCoverB.value);
    this._uniforms.uExposureA.value = this._uniforms.uExposureB.value;
    this._uniforms.uImageB.value = this._blank;
    this._uniforms.uMix.value = 0;
    this._fade = 0;
    this._applyLook(this._current.entry);
    this._darkenKey = '';
    if (outgoing && outgoing.texture) outgoing.texture.dispose();
  }

  dispose() {
    if (activeInstance === this) activeInstance = null;
    // Invalidates any load still in flight, so its texture is disposed on
    // arrival rather than being attached to a visualizer that no longer exists.
    this._loadToken++;
    this._scene.remove(this._mesh);
    this._scene = null;
    this._geometry.dispose();
    this._material.dispose();
    this._blank.dispose();
    if (this._current && this._current.texture) this._current.texture.dispose();
    if (this._incoming && this._incoming.texture) this._incoming.texture.dispose();
    this._current = null;
    this._incoming = null;
  }
}


// Cover fit: the fraction of the texture spanned by the frame on each axis.
// Only ever <= 1 on both, because cover always crops.
function coverFor(imageAspect, screenAspect) {
  return new THREE.Vector2(
    Math.min(1, screenAspect / imageAspect),
    Math.min(1, imageAspect / screenAspect)
  );
}

// See EXPOSURE_MATCH_POWER - a nudge toward the library's mean density, hard
// clamped so a photograph that is dark on purpose stays dark on purpose.
function exposureFor(entry, manifest) {
  if (!entry.meanLuma || !manifest.targetLuma) return 1;
  const raw = Math.pow(manifest.targetLuma / entry.meanLuma, EXPOSURE_MATCH_POWER);
  return Math.min(EXPOSURE_MATCH_MAX, Math.max(EXPOSURE_MATCH_MIN, raw));
}
